/**
 * The communications log client (`/v1/admin/messages`).
 *
 * Its own file rather than more of `admin.ts` for the same reason `products.ts`
 * is: the message log is a slice an operator opens for one kind of question —
 * "did this reach them" — and keeping its calls together is what makes the
 * screen's data dependencies readable at a glance.
 */
import { apiFetch } from "@/lib/api-client";
import type {
  ApiResponse,
  MessageChannel,
  MessageStatus,
  MessageSummary,
  OutboundMessage,
  OutboundMessageDetail,
  Paginated,
} from "@/lib/api/types";

/** Matches `admin.ts` — these are tables, not showcases. */
export const PER_PAGE = 25;

type Query = Record<string, string | number | boolean | undefined>;

function clean(query: Query): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  ) as Record<string, string | number | boolean>;
}

export type MessageQuery = {
  page?: number;
  status?: MessageStatus | undefined;
  channel?: MessageChannel | undefined;
  template?: string | undefined;
  /**
   * Exact address, not a substring — the API matches the whole column so the
   * index is usable. An operator always has the address from the order.
   */
  recipient?: string | undefined;
  orderNumber?: string | undefined;
  /** Folds FAILED and BOUNCED together: the two ways a message did not arrive. */
  failed?: boolean | undefined;
};

export function fetchMessages(
  query: MessageQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<OutboundMessage[]>> {
  return apiFetch<Paginated<OutboundMessage[]>>("/v1/admin/messages", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      status: query.status,
      channel: query.channel,
      template: query.template,
      recipient: query.recipient,
      orderNumber: query.orderNumber,
      failed: query.failed === undefined ? undefined : String(query.failed),
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchMessage(
  id: string,
  signal?: AbortSignal,
): Promise<OutboundMessageDetail> {
  const { data } = await apiFetch<ApiResponse<OutboundMessageDetail>>(
    `/v1/admin/messages/${encodeURIComponent(id)}`,
    { ...(signal ? { signal } : {}) },
  );

  return data;
}

export async function fetchMessageSummary(signal?: AbortSignal): Promise<MessageSummary> {
  const { data } = await apiFetch<ApiResponse<MessageSummary>>("/v1/admin/messages/summary", {
    ...(signal ? { signal } : {}),
  });

  return data;
}

/**
 * The templates present in the log, for the filter dropdown.
 *
 * Read from the data, not from a list in this file: a template that has since
 * been renamed still has rows, and a hard-coded dropdown would make them
 * unreachable through the UI.
 */
export async function fetchTemplates(signal?: AbortSignal): Promise<string[]> {
  const { data } = await apiFetch<ApiResponse<string[]>>("/v1/admin/messages/templates", {
    ...(signal ? { signal } : {}),
  });

  return data;
}

/**
 * Sends the message again as a **new row** linked to the original.
 *
 * Not idempotent, deliberately — pressing it twice sends twice and the log
 * shows two attempts, which is the correct record of what happened. The button
 * guards against a double-click; the API does not pretend the second press was
 * the first.
 *
 * `to` re-addresses it, for the case that makes resend worth more than a
 * retry: the original address was wrong or has since changed.
 */
export async function resendMessage(
  id: string,
  to?: string,
): Promise<{ original: OutboundMessage; resent: OutboundMessage | null }> {
  const body = await apiFetch<{
    data: OutboundMessage;
    meta: { resent: OutboundMessage | null };
  }>(`/v1/admin/messages/${encodeURIComponent(id)}/resend`, {
    body: to ? { to } : {},
  });

  return { original: body.data, resent: body.meta.resent };
}

/** Operator-facing template names. Unknown keys fall back to the raw string. */
const TEMPLATE_LABELS: Record<string, string> = {
  "order-confirmed": "Order confirmed",
  "order-cancelled": "Order cancelled",
};

export function templateLabel(template: string): string {
  return TEMPLATE_LABELS[template] ?? template;
}
